import { useMemo, useRef, useState } from 'react';
import { Panel } from './Panel';
import { useAppState } from '@/ui/AppState';
import {
  BY_Z,
  CATEGORY_COLORS,
  CATEGORY_LABELS,
  ELEMENTS,
  massNoteFor,
  printedCharges,
  searchElements,
  type ChemElement,
} from '@/domain/elements';

/**
 * The table laid out as it is printed: 18 columns, with the lanthanides and
 * actinides on their own rows below, and the sheet's own colour categories.
 */
export function PeriodicTablePanel({ onClose }: { onClose: () => void }) {
  const [selected, setSelected] = useState<ChemElement | null>(null);
  const [query, setQuery] = useState('');
  const field = useRef<HTMLInputElement>(null);

  // Matches light up in place rather than filtering the table down to a list:
  // where an element sits is half of what the table tells you.
  const matches = useMemo(() => searchElements(query), [query]);
  const matched = useMemo(() => new Set(matches.map((e) => e.z)), [matches]);
  const searching = query.trim().length > 0;

  const main = ELEMENTS.filter((e) => e.group !== null);
  const lanthanides = ELEMENTS.filter((e) => e.z >= 57 && e.z <= 71);
  const actinides = ELEMENTS.filter((e) => e.z >= 89 && e.z <= 103);

  return (
    <Panel title="Periodic table" onClose={onClose}>
      <div className="stack">
        {selected ? (
          <ElementDetail element={selected} onBack={() => setSelected(null)} />
        ) : (
          <>
            <p className="faint" style={{ margin: 0 }}>
              Your own table, transcribed from the sheet. Tap an element for its details.
            </p>

            <div className="ptable-search">
              <input
                ref={field}
                className="input"
                type="search"
                value={query}
                spellCheck={false}
                autoComplete="off"
                aria-label="Find an element"
                placeholder="Find: Na, sodium, 11, noble gas"
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  // Escape clears the search rather than closing the panel,
                  // which is what it means while there is something typed.
                  if (e.key === 'Escape' && query) {
                    e.preventDefault();
                    e.stopPropagation();
                    setQuery('');
                  }
                  if (e.key === 'Enter' && matches[0]) {
                    e.preventDefault();
                    setSelected(matches[0]);
                  }
                }}
              />
              {searching ? (
                <button
                  className="btn btn--sm"
                  onClick={() => {
                    setQuery('');
                    field.current?.focus();
                  }}
                >
                  Clear
                </button>
              ) : null}
            </div>

            {searching ? (
              <div className="ptable-hits" role="status">
                {matches.length === 0 ? (
                  <span className="faint">Nothing matches {quoted(query)}.</span>
                ) : (
                  <>
                    <span className="faint">
                      {matches.length} match{matches.length === 1 ? '' : 'es'} —{' '}
                    </span>
                    {matches.slice(0, 8).map((element) => (
                      <button
                        key={element.z}
                        className="chip chip--count"
                        onClick={() => setSelected(element)}
                      >
                        {element.symbol} {element.name}
                      </button>
                    ))}
                    {matches.length > 8 ? (
                      <span className="faint">and {matches.length - 8} more</span>
                    ) : null}
                  </>
                )}
              </div>
            ) : null}

            <div className="ptable-scroll">
              <div
                className={`ptable${searching ? ' ptable--searching' : ''}`}
                role="grid"
                aria-label="Periodic table"
              >
                {main.map((element) => (
                  <ElementCell
                    key={element.z}
                    element={element}
                    onPick={setSelected}
                    match={matched.has(element.z)}
                  />
                ))}
                <div className="ptable__gap" aria-hidden="true" />
                {lanthanides.map((element) => (
                  <ElementCell
                    key={element.z}
                    element={element}
                    onPick={setSelected}
                    row={9}
                    match={matched.has(element.z)}
                  />
                ))}
                {actinides.map((element) => (
                  <ElementCell
                    key={element.z}
                    element={element}
                    onPick={setSelected}
                    row={10}
                    match={matched.has(element.z)}
                  />
                ))}
              </div>
            </div>

            <h3 className="section-title">Key</h3>
            <div className="row">
              {(Object.keys(CATEGORY_LABELS) as Array<keyof typeof CATEGORY_LABELS>).map((key) => (
                <span
                  key={key}
                  className="chip"
                  style={{
                    borderColor: CATEGORY_COLORS[key].light,
                    borderWidth: 2,
                    borderStyle: 'solid',
                  }}
                >
                  {CATEGORY_LABELS[key]}
                </span>
              ))}
            </div>
          </>
        )}
      </div>
    </Panel>
  );
}

/** Quotes what was typed, so the empty-result line reads as a sentence. */
function quoted(query: string): string {
  return `“${query.trim()}”`;
}

function ElementCell({
  element,
  onPick,
  row,
  match,
}: {
  element: ChemElement;
  onPick: (e: ChemElement) => void;
  row?: number;
  /** True while a search is running and this element is one of the hits. */
  match?: boolean;
}) {
  const { theme } = useAppState();
  const colour = CATEGORY_COLORS[element.category][theme];

  // Lanthanum and actinium sit in their series rows, not in group 3.
  const column = row ? ((element.z - (row === 9 ? 57 : 89)) % 15) + 3 : (element.group ?? 1);

  return (
    <button
      className={`ptable__cell${match ? ' ptable__cell--match' : ''}`}
      style={{
        gridColumn: column,
        gridRow: row ?? element.period,
        borderColor: colour,
      }}
      onClick={() => onPick(element)}
      title={`${element.name} — ${CATEGORY_LABELS[element.category]}`}
    >
      <span className="ptable__z">{element.z}</span>
      <span className="ptable__symbol">{element.symbol}</span>
      <span className="ptable__mass">{element.printedMass.replace(/[[\]]/g, '')}</span>
    </button>
  );
}

function ElementDetail({ element, onBack }: { element: ChemElement; onBack: () => void }) {
  const { theme } = useAppState();
  const colour = CATEGORY_COLORS[element.category][theme];
  const charges = printedCharges(element.symbol);
  const massNote = massNoteFor(element.symbol);
  const neighbours = [BY_Z.get(element.z - 1), BY_Z.get(element.z + 1)];

  return (
    <div className="stack">
      <button className="btn btn--sm btn--ghost" onClick={onBack} style={{ alignSelf: 'flex-start' }}>
        ‹ Back to the table
      </button>

      <div className="card stack" style={{ borderColor: colour, borderWidth: 2 }}>
        <div className="spread">
          <div>
            <div className="mono faint">{element.z}</div>
            <div style={{ fontSize: '2.4rem', fontFamily: 'var(--font-head)', lineHeight: 1 }}>
              {element.symbol}
            </div>
            <div style={{ fontWeight: 500 }}>{element.name}</div>
          </div>
          <span className="chip" style={{ borderColor: colour, borderWidth: 2, borderStyle: 'solid' }}>
            {CATEGORY_LABELS[element.category]}
          </span>
        </div>

        {element.printedSymbol ? (
          <div className="banner banner--info">
            <strong>Renamed since your sheet was printed</strong>
            <div>
              It shows {element.printedSymbol} ({element.printedName}). The current name is{' '}
              {element.name}, symbol {element.symbol}.
            </div>
          </div>
        ) : null}

        <Row label="Atomic mass" value={element.printedMass} mono note="as printed on your sheet" />
        {massNote ? <div className="banner banner--warn">{massNote}</div> : null}

        <Row
          label="Common ions"
          value={charges.length ? charges.join(', ') : 'not given'}
          mono
          note="the sheet's own valence charges, exactly as printed"
        />

        <Row
          label="Electronegativity"
          value={element.electronegativity === null ? 'n/a' : String(element.electronegativity)}
          mono
          filled
          note={
            element.electronegativity === null
              ? 'no established Pauling value'
              : 'Pauling scale — not on your sheet'
          }
        />

        <Row label="Electron configuration" value={element.config} mono filled note="not on your sheet" />

        <Row label="Group" value={element.group === null ? '—' : String(element.group)} />
        <Row label="Period" value={String(element.period)} />
      </div>

      <div className="row">
        {neighbours.map((n) =>
          n ? (
            <span key={n.z} className="chip chip--count">
              {n.z} {n.symbol}
            </span>
          ) : null,
        )}
      </div>

      <p className="faint" style={{ margin: 0 }}>
        Values marked <em>filled in</em> are not on your sheet; they come from standard references.
        Everything else is exactly what the sheet prints, including where it is wrong.
      </p>
    </div>
  );
}

function Row({
  label,
  value,
  mono,
  filled,
  note,
}: {
  label: string;
  value: string;
  mono?: boolean;
  filled?: boolean;
  note?: string;
}) {
  return (
    <div className="spread" style={{ alignItems: 'flex-start', gap: 'var(--sp-3)' }}>
      <span className="faint" style={{ minWidth: '9em' }}>
        {label}
        {filled ? <span className="element-filled"> filled in</span> : null}
      </span>
      <span style={{ textAlign: 'right' }}>
        <span className={mono ? 'mono' : undefined} style={{ overflowWrap: 'anywhere' }}>
          {value}
        </span>
        {note ? <div className="faint">{note}</div> : null}
      </span>
    </div>
  );
}
